import { FC, useCallback, useEffect, useRef, useState } from "react";

import { Box, Stack, StackProps, useTheme } from "@mui/material";
import { FormCategory } from "@simplyk/common";

import { CroppedDescription } from "./components/CroppedDescription/CroppedDescription";
import { ExpandedDescription } from "./components/ExpandedDescription/ExpandedDescription";

import { useFormV2Context } from "@/canvas/features/FormV2/contexts/FormV2Context";
import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useSanitizeHtmlString } from "@/common/helpers/html";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { FORM_ELEMENT_IDS } from "@/features/LiveFormEditor/Steps/helpers/formStepToElementIdMap";
import { FormDialog } from "@/forms-design-system/FormDialog";
import { useExpandableContentHeight } from "@/forms-design-system/FormExpandableText/useExpandableContentHeight";
import { FormTextButton } from "@/forms-design-system/FormTextButton";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { ArrowDiagonalRight } from "@/icons/outlined";

type CampaignDescriptionProps = Omit<StackProps, "maxHeight" | "children"> & {
  description?: string;
  isExpanded?: boolean;
};

export const CampaignDescription: FC<CampaignDescriptionProps> = ({
  description: descriptionOverride,
  isExpanded = false,
  ...rest
}) => {
  const theme = useTheme();
  const { isSmallScreen } = useMediaQuery();
  const { t } = useTranslate();
  const { localeField, formCategory, description: descriptionFromContext } = useFormV2Context();
  const { logAmplitudeEvent } = useAmplitude();
  const description = descriptionOverride ?? descriptionFromContext;
  const sanitizedDescriptionRaw = useSanitizeHtmlString(description ?? "");
  const sanitizedDescription = sanitizedDescriptionRaw || "";
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const { isCropped: canBeExpanded, refresh } = useExpandableContentHeight(contentRef);

  const maxHeight = { xs: "40px", md: "80px" } as const;
  const gradientOverlayHeight = "20px";
  const gradientOverlayColor = theme.palette.background.default;
  const shouldDisplayAboutLabel =
    formCategory !== FormCategory.DonationForm && formCategory !== FormCategory.PeerToPeerCampaign && !isSmallScreen;

  const aboutLabel = (() => {
    switch (formCategory) {
      case FormCategory.Event:
      case FormCategory.Custom:
      case FormCategory.Auction:
        return t("ticketing", "description.AboutThisEvent");
      case FormCategory.Shop:
        return t("ticketing", "description.AboutThisShop");
      case FormCategory.Lottery:
        return t("ticketing", "description.AboutThisRaffle");
      case FormCategory.Sweepstakes:
        return t("ticketing", "description.AboutThisSweepstakes");
      case FormCategory.MembershipV2:
        return t("ticketing", "description.AboutThisMembership");
      default:
        return "";
    }
  })();

  useEffect(() => {
    // Refresh the height when the description changes
    refresh();
  }, [description, refresh]);

  const handleOpenDialog = useCallback(() => {
    logAmplitudeEvent(AmplitudeEvents.DonorFormDescriptionDialogOpened, {
      value: "more details",
    });
    setIsDialogOpen(true);
  }, [logAmplitudeEvent]);

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  if (!description) {
    return null;
  }

  if (!theme.palette.forms) {
    return <InvalidThemeErrorMessage />;
  }

  if (isExpanded) {
    return (
      <Stack
        position="relative"
        alignItems="flex-start"
        id={FORM_ELEMENT_IDS["description"]}
        data-test="form-v2-description"
        {...rest}
      >
        {shouldDisplayAboutLabel && (
          <FormTypography variant="body2" vibe="quiet" sx={{ marginBottom: "8px" }}>
            {aboutLabel}
          </FormTypography>
        )}
        <ExpandedDescription sanitizedDescription={sanitizedDescription} />
      </Stack>
    );
  }

  return (
    <>
      <Stack
        position="relative"
        alignItems="flex-start"
        overflow="hidden"
        id={FORM_ELEMENT_IDS["description"]}
        data-test="form-v2-description"
        {...rest}
      >
        {shouldDisplayAboutLabel && (
          <FormTypography variant="body2" vibe="quiet" sx={{ marginBottom: "8px" }}>
            {aboutLabel}
          </FormTypography>
        )}
        <CroppedDescription
          sanitizedDescription={sanitizedDescription}
          contentRef={contentRef}
          maxHeight={maxHeight}
          gradientOverlayHeight={gradientOverlayHeight}
          gradientOverlayColor={gradientOverlayColor}
          showGradient={canBeExpanded}
        />
        {canBeExpanded && (
          <Box sx={{ marginTop: "8px" }}>
            <FormTextButton endIcon={<ArrowDiagonalRight />} onClick={handleOpenDialog}>
              {t("common", "seeMoreDetails")}
            </FormTextButton>
          </Box>
        )}
      </Stack>
      <FormDialog
        title={localeField?.title || ""}
        open={isDialogOpen}
        onClose={handleCloseDialog}
        size="s"
        variant={isSmallScreen ? "drawer" : "default"}
      >
        <Box
          component="div"
          sx={{
            overflowWrap: "anywhere",
            ["& *"]: {
              maxWidth: "100%",
              color: "inherit !important",
            },
            ["& p"]: {
              width: "auto !important",
              marginInline: "0 !important",
            },
          }}
          dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
        />
      </FormDialog>
    </>
  );
};
